/**
 * Web Vitals Reporting
 *
 * Tracks Core Web Vitals metrics for performance monitoring:
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FID (First Input Delay): Interactivity (deprecated, replaced by INP)
 * - FCP (First Contentful Paint): Loading performance
 * - LCP (Largest Contentful Paint): Loading performance
 * - TTFB (Time to First Byte): Server response time
 * - INP (Interaction to Next Paint): Interactivity
 *
 * Metrics are sent to Google Analytics 4 if available, otherwise logged to console.
 */

import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

type WebVitalsHandler = (metric: Metric) => void;

/**
 * Send metric to Google Analytics 4 (if available)
 * Falls back to console logging in development
 */
const sendToAnalytics: WebVitalsHandler = (metric) => {
  // Check if Google Analytics 4 is available
  const gtag = (window as any).gtag;
  
  if (typeof gtag === "function") {
    gtag("event", metric.name, {
      event_category: "Web Vitals",
      event_label: metric.id,
      value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }

  // Log to console in development
  if (process.env.NODE_ENV === "development") {
    const rating = metric.rating;
    const color = rating === "good" ? "green" : rating === "needs-improvement" ? "orange" : "red";
    console.log(
      `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${rating})`,
      `color: ${color}; font-weight: bold;`
    );
  }
};

/**
 * Initialize Web Vitals reporting
 * Call this once when the app starts
 */
export const reportWebVitals = (handler: WebVitalsHandler = sendToAnalytics): void => {
  onCLS(handler);   // Cumulative Layout Shift
  onFCP(handler);   // First Contentful Paint
  onINP(handler);   // Interaction to Next Paint
  onLCP(handler);   // Largest Contentful Paint
  onTTFB(handler);  // Time to First Byte
};

/**
 * Get performance entries for debugging
 */
export const getPerformanceEntries = (): PerformanceEntry[] => {
  if (typeof window !== "undefined" && window.performance) {
    return window.performance.getEntries();
  }
  return [];
};
